import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppState } from 'store/store';
import { practicePenaltiesServices } from 'api/services';
import Template from 'components/Template/Template';
import { MainLoader } from 'components/Loader/Loader';
import { Button, Grid, TextField, Typography } from '@mui/material';
import './UpdatePracticePenaltyPage.sass';
import { ShowNotification } from 'helpers/showNotice';

const UpdatePracticePenaltyPage: React.FC = () => {
  const { practiceExercisesId } = useParams<{ practiceExercisesId: string }>();
  const { practicePenaltyId } = useParams<{ practicePenaltyId: string }>();
  const { vehicleType } = useParams<{ vehicleType: IVehicleType }>();
  const [titleRu, setTitleRu] = useState('');
  const [titleUz, setTitleUz] = useState('');
  const [titleOz, setTitleOz] = useState('');
  const [penaltyPoints, setPenaltyPoints] = useState('');
  const [code, setCode] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const history = useNavigate();
  const i18n = useSelector(
    (state: AppState) => state.i18n.data.pages.UpdatePracticePenaltyPage
  );

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    const res = await practicePenaltiesServices
      .getPracticePenaltyById(Number(practicePenaltyId))
      .then((res) => res.data);
    setTitleRu(res.titleRu);
    setTitleUz(res.titleUz);
    setTitleOz(res.titleOz);
    setPenaltyPoints(res.penaltyPoints ? res.penaltyPoints.toString() : '');
    setCode(res.code);
  };

  const submit = async () => {
    if (!practiceExercisesId) return;
    let validation = true;

    if (
      !penaltyPoints.trim() ||
      !titleRu.trim() ||
      !titleUz.trim() ||
      !titleOz.trim() ||
      !code
    ) {
      validation = false;
    }

    if (!validation) {
      setHasError(true);
      return;
    }

    const params = {
      titleRu,
      titleUz,
      titleOz,
      penaltyPoints: Number(penaltyPoints),
      practiceExerciseId: Number(practiceExercisesId),
      code: Number(code),
    };

    try {
      setLoading(true);
      await practicePenaltiesServices.updatePracticePenalty(
        Number(practicePenaltyId),
        params
      );
      history(
        `/vehicle-types/${vehicleType}/practice-exercise/${practiceExercisesId}/practice-penalties`
      );
    } catch (e: any) {
      setLoading(false);
      if (e.response.data.message === 'practicePenaltyCode') {
        ShowNotification(i18n.codeError);
      }
    }
  };

  return (
    <Template>
      <div className='update-practice-penalty-page'>
        <div className='update-practice-penalty-page__row'>
          <Typography variant='h3'>{i18n.updatePenalty}</Typography>
        </div>
        <Grid container spacing={2} item md={6} xs={12}>
          <Grid item md={12}>
            <TextField
              value={titleUz}
              onChange={(e) => setTitleUz(e.target.value)}
              label={i18n.nameLat}
              size='small'
              error={hasError && !titleUz.trim()}
            />
          </Grid>
          <Grid item md={12}>
            <TextField
              value={titleOz}
              onChange={(e) => setTitleOz(e.target.value)}
              label={i18n.nameCyr}
              size='small'
              error={hasError && !titleOz.trim()}
            />
          </Grid>
          <Grid item md={12}>
            <TextField
              value={titleRu}
              onChange={(e) => setTitleRu(e.target.value)}
              label={i18n.nameRu}
              size='small'
              error={hasError && !titleRu.trim()}
            />
          </Grid>
          <Grid item md={12}>
            <TextField
              value={penaltyPoints}
              onChange={(e) => setPenaltyPoints(e.target.value)}
              label={i18n.points}
              size='small'
              error={hasError && !penaltyPoints.trim()}
              type='number'
            />
          </Grid>
          <Grid item md={12}>
            <TextField
              value={code && code > 0 ? code : ''}
              onChange={(e) => setCode(Number(e.target.value))}
              label={i18n.code}
              size='small'
              autoComplete='off'
              error={hasError && !code}
            />
          </Grid>
        </Grid>
        <Grid
          container
          md={6}
          item
          spacing={2}
          className='update-practice-penalty-page__btn-wrap'
        >
          <Grid item md={6}>
            <Link
              to={`/vehicle-types/${vehicleType}/practice-exercise/${practiceExercisesId}/practice-penalties`}
              className='update-practice-penalty-page__link'
            >
              <Button
                fullWidth
                size='large'
                variant='contained'
                color='inherit'
              >
                {i18n.cancel}
              </Button>
            </Link>
          </Grid>
          <Grid item md={6}>
            <Button
              onClick={submit}
              fullWidth
              size='large'
              variant='contained'
              color='success'
            >
              {loading ? <MainLoader /> : i18n.save}
            </Button>
          </Grid>
        </Grid>
      </div>
    </Template>
  );
};

export default UpdatePracticePenaltyPage;
